import { Check, Copy, Eye, EyeOff, RefreshCw, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import PluginsCard from '../../components/admin/PluginsCard';
import { formatDate } from '../../components/admin/adminUtils';
import {
  isLicenseActive,
  isUnlimited,
  licenseStatusBadge,
  maskLicenseKey,
  paletteForDomain,
  planLabel,
  usageSummary,
} from '../../components/admin/licenseUtils';
import { getLicenses, parseApiError, releaseDomain } from '../../helpers/licenseApi';

function CopyButton({ value, label = 'Copy key' }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      /* clipboard blocked; no-op */
    }
  };
  return (
    <button className="btn btn-ghost btn-sm" type="button" onClick={onCopy} aria-label={label}>
      {copied ? <Check size={14} /> : <Copy size={14} />}
      {copied ? 'Copied' : label}
    </button>
  );
}

// One selectable summary card in the "Your licenses" switcher row.
function LicenseChip({ license, selected, onSelect }) {
  const badge = licenseStatusBadge(license.status);
  return (
    <button
      className={`sh-lic-chip ${selected ? 'is-selected' : ''}`}
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
    >
      <span className="sh-chip-head">
        <span className={`sh-status-dot ${badge.className}`} aria-hidden="true" />
        <span className="sh-chip-plan">{planLabel(license)}</span>
      </span>
      <span className="sh-chip-key">{maskLicenseKey(license.license_key)}</span>
      <span className="sh-chip-usage">{usageSummary(license)}</span>
    </button>
  );
}

// The License & account detail card for the selected license.
function LicenseDetailCard({ license }) {
  const [revealed, setRevealed] = useState(false);
  const badge = licenseStatusBadge(license.status);
  const active = isLicenseActive(license);
  const unlimited = isUnlimited(license);

  // Re-mask whenever the selected license changes.
  useEffect(() => setRevealed(false), [license.license_key]);

  return (
    <section className="panel-card sh-lic-card">
      <div className="card-header">
        <h2>License &amp; account</h2>
        <span className={`sh-plan-chip ${badge.className}`}>{badge.label}</span>
      </div>

      <div className="sh-keyfield">
        <code className="sh-key">
          {revealed ? license.license_key : maskLicenseKey(license.license_key)}
        </code>
        <div className="sh-key-actions">
          <button
            className="btn btn-ghost btn-sm"
            type="button"
            onClick={() => setRevealed((value) => !value)}
            aria-label={revealed ? 'Hide license key' : 'Reveal license key'}
          >
            {revealed ? <EyeOff size={14} /> : <Eye size={14} />}
            {revealed ? 'Hide' : 'Reveal'}
          </button>
          <CopyButton value={license.license_key} />
        </div>
      </div>

      <dl className="sh-lic-meta">
        <div className="sh-lic-row">
          <dt>Account email</dt>
          <dd>{license.email || '—'}</dd>
        </div>
        <div className="sh-lic-row">
          <dt>Plan</dt>
          <dd>{planLabel(license)}</dd>
        </div>
        <div className="sh-lic-row">
          <dt>Updates &amp; support</dt>
          <dd className={active ? 'sh-ok' : ''}>{active ? 'Active ✓' : badge.label}</dd>
        </div>
        <div className="sh-lic-row">
          <dt>Activations</dt>
          <dd>
            {license.domains_used || 0} active · {unlimited ? 'Unlimited' : license.max_allowed_domains || 0}
          </dd>
        </div>
        <div className="sh-lic-row">
          <dt>Plugins available</dt>
          <dd>{license.entitlements?.length || 0}</dd>
        </div>
      </dl>
    </section>
  );
}

// The "Active installs" card — activations table + per-row release.
function ActiveInstallsCard({ license, onReleased }) {
  const [confirmDomain, setConfirmDomain] = useState(null);
  const [busyDomain, setBusyDomain] = useState(null);
  const [error, setError] = useState('');

  const active = isLicenseActive(license);
  const unlimited = isUnlimited(license);
  const activeInstalls = useMemo(
    () => (license.activations || []).filter((row) => row.active),
    [license.activations],
  );

  const confirmRelease = async () => {
    if (!confirmDomain) return;
    setBusyDomain(confirmDomain);
    setError('');
    try {
      await releaseDomain(license.license_key, confirmDomain);
      setConfirmDomain(null);
      await onReleased();
    } catch (requestError) {
      setError(parseApiError(requestError, 'Could not release that site.'));
    } finally {
      setBusyDomain(null);
    }
  };

  return (
    <section className="panel-card sh-act-card">
      <div className="card-header">
        <h2>Active installs</h2>
        <span className="sh-count">
          {activeInstalls.length} {activeInstalls.length === 1 ? 'site' : 'sites'}
        </span>
      </div>

      {error ? <div className="error-banner">{error}</div> : null}

      {activeInstalls.length ? (
        <div className="sh-act-list">
          {activeInstalls.map((row) => (
            <div className="sh-act-row" key={row.domain}>
              <span className={`sh-fav ${paletteForDomain(row.domain)}`} aria-hidden="true">
                {(row.domain || '?').charAt(0).toUpperCase()}
              </span>
              <span className="sh-dom">
                <span className="sh-d">{row.domain}</span>
                <span className="sh-meta">Activated {formatDate(row.activated_at)}</span>
              </span>
              {active ? (
                <button
                  className="btn btn-ghost btn-sm"
                  type="button"
                  onClick={() => setConfirmDomain(row.domain)}
                  disabled={busyDomain === row.domain}
                >
                  {busyDomain === row.domain ? 'Releasing…' : 'Deactivate'}
                </button>
              ) : null}
            </div>
          ))}
        </div>
      ) : (
        <div className="table-empty">No active installs for this license.</div>
      )}

      <div className="sh-act-foot">
        <span className="sh-status-dot active" aria-hidden="true" />
        {unlimited
          ? 'Unlimited plan — activate on as many sites as you run.'
          : `${license.domains_used || 0} of ${license.max_allowed_domains || 0} sites used.`}
      </div>

      {confirmDomain ? (
        <div className="modal-backdrop" role="presentation">
          <section
            className="create-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="release-title"
          >
            <header className="modal-head">
              <div className="modal-head-copy">
                <div className="modal-kicker">
                  <span>RELEASE SITE</span>
                  {confirmDomain}
                </div>
              </div>
              <button
                className="icon-button"
                type="button"
                onClick={() => setConfirmDomain(null)}
                aria-label="Close release dialog"
              >
                <X size={16} />
              </button>
            </header>
            <div className="modal-body">
              <h2 id="release-title">Release {confirmDomain}?</h2>
              <p>
                This frees an activation slot. The live site at <strong>{confirmDomain}</strong> will
                fail license validation until you re-activate it from the WordPress install.
              </p>
            </div>
            <footer className="modal-foot">
              <span>FREES ONE ACTIVATION SLOT</span>
              <div>
                <button
                  className="btn btn-ghost"
                  type="button"
                  onClick={() => setConfirmDomain(null)}
                  disabled={busyDomain === confirmDomain}
                >
                  Keep active
                </button>
                <button
                  className="btn btn-danger"
                  type="button"
                  onClick={confirmRelease}
                  disabled={busyDomain === confirmDomain}
                >
                  {busyDomain === confirmDomain ? 'Releasing…' : 'Release site'}
                </button>
              </div>
            </footer>
          </section>
        </div>
      ) : null}
    </section>
  );
}

export default function SelfHostedPage() {
  const [licenses, setLicenses] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadLicenses = useCallback(async () => {
    setError('');
    try {
      const data = await getLicenses();
      setLicenses(data);
      setSelectedKey((current) => {
        if (current && data.some((license) => license.license_key === current)) return current;
        return data[0]?.license_key || null;
      });
    } catch (requestError) {
      setError(parseApiError(requestError, 'Could not load your licenses.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLicenses();
  }, [loadLicenses]);

  const selected = useMemo(
    () => licenses.find((license) => license.license_key === selectedKey) || licenses[0] || null,
    [licenses, selectedKey],
  );

  const count = licenses.length;
  const subtitle = loading
    ? 'Your license, plugin downloads, and the full version archive.'
    : count === 0
      ? 'No licenses on this account yet.'
      : count === 1
        ? 'Your license, plugin downloads, and the full version archive — for sites you host yourself.'
        : `You hold ${count} licenses — pick one to manage its key, activations, and downloads.`;

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Self-Hosted</h1>
          <div className="page-subtitle">{subtitle}</div>
        </div>
        <div className="page-actions">
          <button className="btn btn-ghost" type="button" onClick={loadLicenses}>
            <RefreshCw size={15} />
            Refresh
          </button>
        </div>
      </div>

      {loading ? <div className="loading-block">Loading your licenses</div> : null}
      {error ? <div className="error-banner admin-page-error">{error}</div> : null}

      {!loading && !error && count === 0 ? (
        <section className="panel-card sh-empty-card">
          <div className="sh-empty">
            <h3>No licenses found</h3>
            <p>
              We couldn&apos;t find any licenses on this account. If you purchased a license under a
              different email, contact support to have it linked.
            </p>
          </div>
        </section>
      ) : null}

      {!loading && selected ? (
        <>
          {count > 1 ? (
            <section className="sh-licenses">
              <div className="sh-licenses-label">Your licenses ({count})</div>
              <div className="sh-licenses-row">
                {licenses.map((license) => (
                  <LicenseChip
                    key={license.license_key}
                    license={license}
                    selected={license.license_key === selected.license_key}
                    onSelect={() => setSelectedKey(license.license_key)}
                  />
                ))}
              </div>
            </section>
          ) : null}

          <div className="sh-detail-grid">
            <LicenseDetailCard license={selected} />
            <ActiveInstallsCard license={selected} onReleased={loadLicenses} />
          </div>

          <PluginsCard />
        </>
      ) : null}
    </>
  );
}
